/**
 * Translates the structured agency theme (`WlContainer.colors` / `.theme`)
 * into the v2 design tokens and the handful of rules that have no token.
 *
 * Values are agency-authored, so every one of them is sanitized before it
 * reaches a stylesheet: a raw `;` or `}` would let a color field close the
 * declaration and inject arbitrary rules.
 */

import {
  wlCanvasPatternToJSON,
  wlCardElevationToJSON,
  wlSidebarStyleToJSON,
  wlTextTransformToJSON,
} from '@enrollhere/classes';
import type { WlColorContainer, WlThemeSettings } from './wl.service';

const EDITABLE_FONT_HOSTS = ['fonts.googleapis.com', 'fonts.gstatic.com'];

// Matches classic settings: stored storage URLs still load, but edits are
// restricted to Google Fonts by the server and isEditableFontStylesheetUrl.
const CLASSIC_RUNTIME_FONT_HOSTS = [
  ...EDITABLE_FONT_HOSTS,
  'firebasestorage.googleapis.com',
  'storage.googleapis.com',
];

const UNSAFE_VALUE = /[;{}<>\\"]/;
/**
 * Color values legitimately use function syntax (`oklch()`, `color-mix()`),
 * so parentheses are allowed — but only for functions that cannot fetch a
 * remote resource or evaluate script.
 */
const ALLOWED_FUNCTIONS = new Set([
  'rgb',
  'rgba',
  'hsl',
  'hsla',
  'hwb',
  'lab',
  'lch',
  'oklab',
  'oklch',
  'color',
  'color-mix',
  'var',
  'calc',
  'clamp',
  'min',
  'max',
]);
const FUNCTION_NAME = /([a-zA-Z-]*)\(/g;
const MAX_VALUE_LENGTH = 120;

export type ThemeTokens = ReadonlyMap<string, string>;

export function sanitizeCssValue(
  value: string | undefined
): string | undefined {
  const trimmed = value?.trim();
  if (!trimmed || trimmed.length > MAX_VALUE_LENGTH) {
    return undefined;
  }
  if (UNSAFE_VALUE.test(trimmed)) {
    return undefined;
  }
  if (!hasBalancedParens(trimmed)) {
    return undefined;
  }
  for (const match of trimmed.matchAll(FUNCTION_NAME)) {
    if (!ALLOWED_FUNCTIONS.has(match[1].toLowerCase())) {
      return undefined;
    }
  }
  return trimmed;
}

function hasBalancedParens(value: string): boolean {
  let depth = 0;
  for (const char of value) {
    if (char === '(') {
      depth += 1;
    } else if (char === ')') {
      depth -= 1;
      if (depth < 0) {
        return false;
      }
    }
  }
  return depth === 0;
}

/** Quotes a family name so `Space Mono` does not need author-side quoting. */
export function sanitizeFontFamily(
  value: string | undefined
): string | undefined {
  const family = sanitizeCssValue(value);
  if (!family) {
    return undefined;
  }
  return family
    .split(',')
    .map(part => part.trim())
    .filter(Boolean)
    .map(part => (/^[a-zA-Z-]+$/.test(part) ? part : `'${part}'`))
    .join(', ');
}

function fontStylesheetOnHosts(
  value: string | undefined,
  hosts: readonly string[]
): string | undefined {
  const raw = value?.trim();
  if (!raw) {
    return undefined;
  }
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return undefined;
  }
  if (url.protocol !== 'https:') {
    return undefined;
  }
  if (!hosts.includes(url.hostname.toLowerCase())) {
    return undefined;
  }
  return url.toString();
}

/** Stylesheet link href. Includes storage hosts so a saved font still loads. */
export function sanitizeFontStylesheetUrl(
  value: string | undefined
): string | undefined {
  return fontStylesheetOnHosts(value, CLASSIC_RUNTIME_FONT_HOSTS);
}

/** New edits. The server rejects a storage host that was not already stored. */
export function isEditableFontStylesheetUrl(
  value: string | undefined
): boolean {
  return fontStylesheetOnHosts(value, EDITABLE_FONT_HOSTS) !== undefined;
}

const TEXT_TRANSFORMS: Record<string, string> = {
  WL_TEXT_TRANSFORM_NONE: 'none',
  WL_TEXT_TRANSFORM_UPPERCASE: 'uppercase',
  WL_TEXT_TRANSFORM_LOWERCASE: 'lowercase',
  WL_TEXT_TRANSFORM_CAPITALIZE: 'capitalize',
};

const CARD_SHADOWS: Record<string, string> = {
  WL_CARD_ELEVATION_NONE: 'none',
  WL_CARD_ELEVATION_SUBTLE: '0 1px 2px rgb(0 0 0 / 0.06)',
  WL_CARD_ELEVATION_RAISED: '0 8px 24px rgb(0 0 0 / 0.12)',
};

function protoEnumName(
  value: string | number | undefined,
  toJSON: (value: number) => string
): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  return typeof value === 'number' ? toJSON(value) : value;
}

function textTransform(value: string | number | undefined): string | undefined {
  const name = protoEnumName(value, wlTextTransformToJSON);
  return name ? TEXT_TRANSFORMS[name] : undefined;
}

/**
 * Semantic color fields mapped onto the tokens the portal already reads, so
 * each control drives real styling instead of a bespoke selector.
 */
const COLOR_TOKENS: ReadonlyArray<[keyof WlColorContainer, string]> = [
  ['accent', '--btn-primary'],
  ['accentHover', '--btn-primary-hover'],
  ['accentActive', '--btn-primary-active'],
  ['accentContent', '--btn-primary-fg'],
  ['primaryContent', '--color-primary-content'],
  ['background', '--color-base-200'],
  ['surface', '--color-base-100'],
  ['text', '--color-base-content'],
  ['border', '--color-base-300'],
  ['sidebarContent', '--color-sb-content'],
  ['sidebarIcon', '--color-sb-icon'],
  ['sidebarFocus', '--color-sb-focus'],
];

/** Sidebar presets keep the common cases one click away from the picker. */
const SIDEBAR_PRESETS: Record<string, string | undefined> = {
  WL_SIDEBAR_STYLE_LIGHT: 'var(--color-base-200)',
  WL_SIDEBAR_STYLE_INK: 'oklch(21% 0.006 285)',
  WL_SIDEBAR_STYLE_BRAND: undefined,
};

export function resolveSidebarSurface(
  theme: WlThemeSettings | undefined,
  brandColor: string
): string | undefined {
  const style = protoEnumName(theme?.sidebarStyle, wlSidebarStyleToJSON);
  if (!style || style === 'WL_SIDEBAR_STYLE_UNKNOWN') {
    return undefined;
  }
  if (style === 'WL_SIDEBAR_STYLE_CUSTOM') {
    return sanitizeCssValue(theme?.sidebarColor);
  }
  if (style === 'WL_SIDEBAR_STYLE_BRAND') {
    return brandColor;
  }
  return SIDEBAR_PRESETS[style];
}

/**
 * Token overrides for the structured settings. Returned as a map so the
 * caller can merge them after the derived primary-color tokens and let the
 * agency's explicit choices win.
 */
export function buildThemeTokens(
  colors: WlColorContainer | undefined,
  theme: WlThemeSettings | undefined,
  brandColor: string
): ThemeTokens {
  const tokens = new Map<string, string>();

  for (const [field, token] of COLOR_TOKENS) {
    const value = sanitizeCssValue(colors?.[field] as string | undefined);
    if (value) {
      tokens.set(token, value);
    }
  }

  const border = sanitizeCssValue(colors?.border);
  if (border) {
    tokens.set('--color-border', border);
  }

  const sidebarSurface = resolveSidebarSurface(theme, brandColor);
  if (sidebarSurface) {
    tokens.set('--color-sb', sidebarSurface);
  }

  const cornerRadius = sanitizeCssValue(theme?.cornerRadius);
  if (cornerRadius) {
    tokens.set('--radius-box', cornerRadius);
  }

  const controlRadius = sanitizeCssValue(theme?.controlCornerRadius);
  if (controlRadius) {
    tokens.set('--radius-field', controlRadius);
  }

  return tokens;
}

/**
 * Rules for the settings that are not expressible as a token: type styles,
 * card elevation and the optional canvas pattern.
 */
export function buildThemeStylesheet(
  colors: WlColorContainer | undefined,
  theme: WlThemeSettings | undefined
): string {
  const blocks: string[] = [];

  const bodyFont = sanitizeFontFamily(theme?.bodyFont);
  if (bodyFont) {
    blocks.push(`body { font-family: ${bodyFont}, sans-serif; }`);
  }

  blocks.push(buildHeadingRule(theme));
  blocks.push(buildTableHeaderRule(colors, theme));
  blocks.push(buildCardRule(theme));
  blocks.push(buildCanvasRule(theme));

  const activeColor = sanitizeCssValue(colors?.sidebarActiveBackground);
  if (activeColor) {
    blocks.push(`.eh-sidebar-item.active { background: ${activeColor}; }`);
  }

  const activeContent = sanitizeCssValue(colors?.sidebarActiveContent);
  if (activeContent) {
    blocks.push(
      '.eh-sidebar-item.active .eh-sidebar-label,\n' +
        '.eh-sidebar-item.active .eh-sidebar-icon ' +
        `{ color: ${activeContent}; }`
    );
  }

  const cardIcon = sanitizeCssValue(colors?.cardIcon);
  if (cardIcon) {
    blocks.push(`.card-primary .text-primary { color: ${cardIcon}; }`);
  }

  const link = sanitizeCssValue(colors?.link);
  if (link) {
    blocks.push(`.link-primary { color: ${link}; }`);
  }

  const linkHover = sanitizeCssValue(colors?.linkHover);
  if (linkHover) {
    blocks.push(`.link-primary:hover { color: ${linkHover}; }`);
  }

  return blocks.filter(Boolean).join('\n');
}

function buildHeadingRule(theme: WlThemeSettings | undefined): string {
  const declarations = [
    declaration(
      'font-family',
      sanitizeFontFamily(theme?.headingFont),
      ', sans-serif'
    ),
    declaration('font-weight', sanitizeCssValue(theme?.headingWeight)),
    declaration(
      'letter-spacing',
      sanitizeCssValue(theme?.headingLetterSpacing)
    ),
    declaration('text-transform', textTransform(theme?.headingTextTransform)),
  ].filter(Boolean);

  if (declarations.length === 0) {
    return '';
  }
  return `h1,\nh2 { ${declarations.join(' ')} }`;
}

function buildTableHeaderRule(
  colors: WlColorContainer | undefined,
  theme: WlThemeSettings | undefined
): string {
  const declarations = [
    declaration('color', sanitizeCssValue(colors?.tableHeader)),
    declaration(
      'font-family',
      sanitizeFontFamily(theme?.tableHeaderFont),
      ', monospace'
    ),
    declaration('font-size', sanitizeCssValue(theme?.tableHeaderFontSize)),
    declaration(
      'letter-spacing',
      sanitizeCssValue(theme?.tableHeaderLetterSpacing)
    ),
    declaration(
      'text-transform',
      textTransform(theme?.tableHeaderTextTransform)
    ),
  ].filter(Boolean);

  if (declarations.length === 0) {
    return '';
  }
  return `.table thead th { ${declarations.join(' ')} }`;
}

function buildCardRule(theme: WlThemeSettings | undefined): string {
  const elevation = protoEnumName(theme?.cardElevation, wlCardElevationToJSON);
  const shadow = elevation ? CARD_SHADOWS[elevation] : undefined;
  if (!shadow) {
    return '';
  }
  return `.card { box-shadow: ${shadow}; }`;
}

function buildCanvasRule(theme: WlThemeSettings | undefined): string {
  if (
    protoEnumName(theme?.canvasPattern, wlCanvasPatternToJSON) !==
    'WL_CANVAS_PATTERN_DOTS'
  ) {
    return '';
  }

  const color =
    sanitizeCssValue(theme?.canvasPatternColor) ?? 'rgb(0 0 0 / 5%)';
  const size = sanitizeCssValue(theme?.canvasPatternSize) ?? '5px';
  const dot = sanitizeCssValue(theme?.canvasPatternDotSize) ?? '1px';

  return (
    'body { background-image: radial-gradient(' +
    `${color} ${dot}, transparent ${dot}); ` +
    `background-size: ${size} ${size}; }`
  );
}

function declaration(
  property: string,
  value: string | undefined,
  suffix = ''
): string {
  return value ? `${property}: ${value}${suffix};` : '';
}
